/**
 * GridMap parser: the Node3D surface plus the mesh library reference, cell size and scale
 * and the PackedInt32Array body of `data = { "cells": PackedInt32Array(...) }`.
 */
import { parseNode3D } from '../../base/node3d/parser';
import { boolOr, floatOr, vec3Or } from '../../../parser/valueParsers';
import { readCellsField } from './cellData.js';
import { splitTopLevel } from '../../../godot/string/string.js';
import { arrayLiteralBody } from '../../../godot/variant/variantParser.js';
import { holdsResource } from '../../../godot/scene/resourceLiteral.js';
const DEFAULT_CELL_SIZE = { x: 2, y: 2, z: 2 };
/**
 * Shared all-centred default, like DEFAULT_CELL_SIZE. The instance-matrix memo
 * keys on its identity, so a fresh object per parse (every debounced keystroke)
 * would rebuild every cell matrix and the InstancedMesh's GPU buffer.
 */
const DEFAULT_CELL_CENTER = Object.freeze({ x: true, y: true, z: true });
/** Pull the int body out of `... "cells": PackedInt32Array( <body> ) ...`. */
function extractCells(dataProperty) {
    if (!dataProperty)
        return '';
    return readCellsField(dataProperty)?.trim() ?? '';
}
/**
 * Godot defaults every axis to centred and writes the property only when it is
 * off, so an absent key means true. Nothing overridden returns
 * DEFAULT_CELL_CENTER.
 */
function parseCellCenter(properties) {
    if (properties.cell_center_x === undefined &&
        properties.cell_center_y === undefined &&
        properties.cell_center_z === undefined) {
        return DEFAULT_CELL_CENTER;
    }
    return {
        x: boolOr(properties.cell_center_x, true, 'GridMap.cell_center_x'),
        y: boolOr(properties.cell_center_y, true, 'GridMap.cell_center_y'),
        z: boolOr(properties.cell_center_z, true, 'GridMap.cell_center_z'),
    };
}
export function parseGridMap(heading, properties) {
    const baseProperties = parseNode3D(heading, properties);
    return {
        ...baseProperties,
        meshLibrary: properties.mesh_library,
        cellSize: vec3Or(properties.cell_size, DEFAULT_CELL_SIZE, 'GridMap.cell_size'),
        cellCenter: parseCellCenter(properties),
        cellScale: floatOr(properties.cell_scale, 1, 'GridMap.cell_scale'),
        cells: extractCells(properties.data),
        bakedMeshes: bakedMeshRefs(properties.baked_meshes),
    };
}
/** Each baked mesh's reference. The setter skips a null mesh, so the next takes its index (`grid_map.cpp:97`). */
function bakedMeshRefs(raw) {
    const body = raw === undefined ? null : arrayLiteralBody(raw);
    if (body === null)
        return [];
    return splitTopLevel(body)
        .filter((element) => holdsResource(element))
        .map((element) => element.trim());
}
//# sourceMappingURL=parser.js.map